import { calculateMonthlyPayment } from '../calculations'

interface CarAffordabilityInput {
  grossMonthlyIncome: number
  vehiclePrice: number
  downPayment: number
  interestRate: number // annual %
  loanTerm: number // months
  monthlyInvestment: number // what you invest per month
}

export function calculateCarAffordability(input: CarAffordabilityInput) {
  const loanAmount = Math.max(0, input.vehiclePrice - input.downPayment)
  const monthlyPayment = loanAmount > 0
    ? calculateMonthlyPayment(loanAmount, input.interestRate, input.loanTerm)
    : 0

  const downPaymentPercent = input.vehiclePrice > 0
    ? (input.downPayment / input.vehiclePrice) * 100
    : 0

  // 20/3/8 Rule Checks
  const rule20 = downPaymentPercent >= 20
  const rule3 = input.loanTerm <= 36
  const rule8 = input.grossMonthlyIncome > 0
    ? (monthlyPayment / input.grossMonthlyIncome) * 100 <= 8
    : false
  const bonusInvestment = input.monthlyInvestment >= monthlyPayment

  const paymentPercentOfIncome = input.grossMonthlyIncome > 0
    ? (monthlyPayment / input.grossMonthlyIncome) * 100
    : 0

  const allRulesPass = rule20 && rule3 && rule8
  const score = [rule20, rule3, rule8, bonusInvestment].filter(Boolean).length

  // Max affordable price based on 8% rule
  const maxMonthlyPayment = input.grossMonthlyIncome * 0.08
  let maxLoanAmount = 0
  if (input.interestRate > 0) {
    const r = input.interestRate / 100 / 12
    const n = input.loanTerm
    maxLoanAmount = maxMonthlyPayment * (Math.pow(1 + r, n) - 1) / (r * Math.pow(1 + r, n))
  } else {
    maxLoanAmount = maxMonthlyPayment * input.loanTerm
  }
  const maxAffordablePrice = maxLoanAmount / 0.8 // assumes 20% down

  // Total cost of ownership
  const totalInterest = (monthlyPayment * input.loanTerm) - loanAmount
  const totalCost = input.vehiclePrice + Math.max(0, totalInterest)

  // Comparison table: different terms
  const termComparisons = [24, 36, 48, 60, 72].map(term => {
    const pmt = loanAmount > 0 ? calculateMonthlyPayment(loanAmount, input.interestRate, term) : 0
    const interest = (pmt * term) - loanAmount
    const pctIncome = input.grossMonthlyIncome > 0 ? (pmt / input.grossMonthlyIncome) * 100 : 0
    return {
      term,
      monthlyPayment: Math.round(pmt),
      totalInterest: Math.round(Math.max(0, interest)),
      percentOfIncome: Math.round(pctIncome * 10) / 10,
      passes8Rule: pctIncome <= 8,
      passes3Rule: term <= 36,
    }
  })

  return {
    vehiclePrice: input.vehiclePrice,
    downPayment: input.downPayment,
    downPaymentPercent: Math.round(downPaymentPercent * 10) / 10,
    loanAmount: Math.round(loanAmount),
    monthlyPayment: Math.round(monthlyPayment),
    paymentPercentOfIncome: Math.round(paymentPercentOfIncome * 10) / 10,
    rule20,
    rule3,
    rule8,
    bonusInvestment,
    allRulesPass,
    score,
    maxAffordablePrice: Math.round(maxAffordablePrice),
    maxMonthlyPayment: Math.round(maxMonthlyPayment),
    totalInterest: Math.round(Math.max(0, totalInterest)),
    totalCost: Math.round(totalCost),
    termComparisons,
  }
}
